/**
 * WorkoutCalendar — Month-view calendar showing workout activity.
 *
 * Features:
 * - Grid of days for the current month
 * - Colored dots for workout days (bigger/brighter for multiple workouts)
 * - Today highlighted with accent border
 * - Tappable days show summary popover
 * - Arrow navigation between months
 * - Streak counter at the top
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  Modal,
  Pressable,
  useWindowDimensions,
} from 'react-native';
import { ChevronLeft, ChevronRight, Flame, X } from 'lucide-react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { TYPO } from '@/constants/typography';
import { FONT, RADIUS, SPACE } from '@/constants/tokens';
import {
  getMonthWorkouts,
  calculateStreak,
  type MonthWorkoutMap,
  type DaySummary,
} from '@/lib/workoutCalendar';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

interface WorkoutCalendarProps {
  onDayPress?: (dateStr: string, summary: DaySummary | null) => void;
}

export default function WorkoutCalendar({ onDayPress }: WorkoutCalendarProps) {
  const { width: windowWidth } = useWindowDimensions();
  const CELL_SIZE = Math.floor((windowWidth - 32) / 7);
  const { theme } = useTheme();
  const c = theme.colors;
  const { profile } = useAuth();

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [data, setData] = useState<MonthWorkoutMap>({});
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState<{ dateStr: string; summary: DaySummary } | null>(null);
  const [streak, setStreak] = useState(0);

  const loadMonth = useCallback(async () => {
    if (!profile?.id) return;
    setLoading(true);
    try {
      const result = await getMonthWorkouts(profile.id, year, month);
      setData(result);

      // Calculate streak only for current month
      const today = new Date();
      if (year === today.getFullYear() && month === today.getMonth()) {
        setStreak(calculateStreak(result, today));
      } else {
        setStreak(0);
      }
    } catch {
      setData({});
    } finally {
      setLoading(false);
    }
  }, [profile?.id, year, month]);

  useEffect(() => {
    loadMonth();
  }, [loadMonth]);

  const goToPrevMonth = () => {
    if (month === 0) {
      setMonth(11);
      setYear(year - 1);
    } else {
      setMonth(month - 1);
    }
  };

  const goToNextMonth = () => {
    const today = new Date();
    // Don't go past current month
    if (year === today.getFullYear() && month >= today.getMonth()) return;
    if (month === 11) {
      setMonth(0);
      setYear(year + 1);
    } else {
      setMonth(month + 1);
    }
  };

  // Build calendar grid
  const firstDay = new Date(year, month, 1).getDay(); // 0=Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const todayStr = now.toISOString().split('T')[0];

  const isCurrentMonth = year === now.getFullYear() && month === now.getMonth();
  const isFutureDisabled = year === now.getFullYear() && month >= now.getMonth();

  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  // Fill remaining cells to complete last row
  while (cells.length % 7 !== 0) cells.push(null);

  const handleDayPress = (day: number) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const summary = data[dateStr] || null;
    if (summary) {
      setSelectedDay({ dateStr, summary });
    }
    onDayPress?.(dateStr, summary);
  };

  const formatVolume = (vol: number): string => {
    if (vol >= 1000) return `${(vol / 1000).toFixed(1).replace(/\.0$/, '')}k`;
    return String(Math.round(vol));
  };

  return (
    <View style={styles.container}>
      {/* Streak banner */}
      {isCurrentMonth && streak > 0 && (
        <View style={[styles.streakBanner, { backgroundColor: c.surfaceVariant }]}>
          <Flame size={18} color={c.accent} />
          <Text style={[styles.streakText, { color: c.accent }]}>
            Current streak: {streak} day{streak !== 1 ? 's' : ''}
          </Text>
        </View>
      )}

      {/* Month header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={goToPrevMonth} style={styles.arrowBtn} hitSlop={12}>
          <ChevronLeft size={24} color={c.text} />
        </TouchableOpacity>
        <Text style={[styles.monthTitle, { color: c.text }]}>
          {MONTH_NAMES[month]} {year}
        </Text>
        <TouchableOpacity
          onPress={goToNextMonth}
          style={[styles.arrowBtn, isFutureDisabled && { opacity: 0.3 }]}
          hitSlop={12}
          disabled={isFutureDisabled}
        >
          <ChevronRight size={24} color={c.text} />
        </TouchableOpacity>
      </View>

      {/* Weekday labels */}
      <View style={styles.weekRow}>
        {WEEKDAYS.map((d) => (
          <View key={d} style={styles.weekCell}>
            <Text style={[styles.weekLabel, { color: c.textTertiary }]}>{d}</Text>
          </View>
        ))}
      </View>

      {/* Day grid */}
      {loading ? (
        <View style={styles.loadingWrap}>
          <ActivityIndicator size="small" color={c.accent} />
        </View>
      ) : (
        <View style={styles.grid}>
          {cells.map((day, idx) => {
            if (day === null) {
              return <View key={`empty-${idx}`} style={[styles.dayCell, { width: CELL_SIZE, height: CELL_SIZE }]} />;
            }

            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const summary = data[dateStr];
            const isToday = dateStr === todayStr;
            const hasWorkout = !!summary;
            const multiWorkout = summary && summary.count > 1;

            return (
              <Pressable
                key={dateStr}
                style={[
                  styles.dayCell,
                  { width: CELL_SIZE, height: CELL_SIZE },
                  isToday && { borderColor: c.accent, borderWidth: 2, borderRadius: RADIUS.sm },
                ]}
                onPress={() => handleDayPress(day)}
              >
                <Text
                  style={[
                    styles.dayNumber,
                    { color: hasWorkout ? c.text : c.textTertiary },
                    isToday && { color: c.accent },
                  ]}
                >
                  {day}
                </Text>
                {hasWorkout && (
                  <View
                    style={[
                      styles.dot,
                      {
                        backgroundColor: c.accent,
                        width: multiWorkout ? 10 : 6,
                        height: multiWorkout ? 10 : 6,
                        borderRadius: multiWorkout ? 5 : 3,
                        opacity: multiWorkout ? 1 : 0.7,
                      },
                    ]}
                  />
                )}
              </Pressable>
            );
          })}
        </View>
      )}

      {/* Day summary popover */}
      <Modal
        visible={!!selectedDay}
        transparent
        animationType="fade"
        onRequestClose={() => setSelectedDay(null)}
      >
        <Pressable style={styles.overlay} onPress={() => setSelectedDay(null)}>
          <View style={[styles.popover, { backgroundColor: c.surface, borderColor: c.border }]}>
            {selectedDay && (
              <>
                <View style={styles.popoverHeader}>
                  <Text style={[styles.popoverDate, { color: c.text }]}>
                    {new Date(selectedDay.dateStr + 'T12:00:00').toLocaleDateString('en-US', {
                      weekday: 'long',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </Text>
                  <TouchableOpacity onPress={() => setSelectedDay(null)}>
                    <X size={18} color={c.textSecondary} />
                  </TouchableOpacity>
                </View>
                <Text style={[styles.popoverStat, { color: c.accent }]}>
                  {selectedDay.summary.count} workout{selectedDay.summary.count !== 1 ? 's' : ''} —{' '}
                  {formatVolume(selectedDay.summary.totalVolume)} lbs volume
                </Text>
                {selectedDay.summary.exercises.length > 0 && (
                  <Text style={[styles.popoverExercises, { color: c.textSecondary }]} numberOfLines={3}>
                    {selectedDay.summary.exercises.join(', ')}
                  </Text>
                )}
              </>
            )}
          </View>
        </Pressable>
      </Modal>
    </View>
  );
}

// CELL_SIZE is now computed inside the component using useWindowDimensions

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  streakBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: SPACE['2'],
    marginBottom: SPACE['2'],
    borderRadius: RADIUS.sm,
    gap: 6,
  },
  streakText: {
    fontFamily: TYPO.body.semibold,
    fontSize: FONT.base,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: SPACE['2'],
    marginBottom: SPACE['4'],
  },
  arrowBtn: {
    padding: SPACE['1'],
  },
  monthTitle: {
    fontFamily: TYPO.heading.bold,
    fontSize: FONT['2xl'],
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  weekRow: {
    flexDirection: 'row',
    marginBottom: SPACE['1'],
  },
  weekCell: {
    alignItems: 'center',
  },
  weekLabel: {
    fontFamily: TYPO.body.medium,
    fontSize: FONT.xs,
    textTransform: 'uppercase',
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  dayCell: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 2,
  },
  dayNumber: {
    fontFamily: TYPO.body.regular,
    fontSize: FONT.sm,
  },
  dot: {
    marginTop: 2,
  },
  loadingWrap: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Popover
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.6)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  popover: {
    width: '80%',
    maxWidth: 320,
    borderRadius: RADIUS.md,
    borderWidth: 1,
    padding: SPACE['6'],
  },
  popoverHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: SPACE['2'],
  },
  popoverDate: {
    fontFamily: TYPO.heading.semibold,
    fontSize: FONT.lg,
  },
  popoverStat: {
    fontFamily: TYPO.body.semibold,
    fontSize: FONT.base,
    marginBottom: SPACE['1'],
  },
  popoverExercises: {
    fontFamily: TYPO.body.regular,
    fontSize: FONT.sm,
    lineHeight: 18,
  },
});


